import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const optionalDate = isoDate.nullable().optional();
const atLeastOne = (v) => Object.keys(v).length > 0;
/** Money that may be zero (a free stop, a free inspection). */
const feeSchema = z.number().int().min(0).max(1_000_000_000_000);
const time = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (24 hours)')
    .nullable()
    .optional();
// ------------------------------------------------------------------ vehicles
export const VEHICLE_TYPES = ['bus', 'van', 'car', 'other'];
export const VEHICLE_STATUSES = ['active', 'maintenance', 'retired'];
const vehicleFields = {
    registrationNumber: text(20).min(1),
    vehicleType: z.enum(VEHICLE_TYPES),
    makeModel: optionalText(100),
    capacity: z.number().int().min(1).max(200),
    status: z.enum(VEHICLE_STATUSES),
    insuranceExpiry: optionalDate,
    fitnessExpiry: optionalDate,
    notes: optionalText(500),
};
export const createVehicleSchema = z.object({
    ...vehicleFields,
    vehicleType: vehicleFields.vehicleType.default('bus'),
    status: vehicleFields.status.default('active'),
});
export const updateVehicleSchema = z
    .object(vehicleFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const listVehiclesQuerySchema = paginationQuerySchema.extend({
    q: text(50).optional(),
    status: z.enum(VEHICLE_STATUSES).optional(),
});
// ------------------------------------------------------------------- drivers
const driverFields = {
    staffId: uuidSchema.nullable().optional(),
    fullName: text(200).min(1),
    phone: text(30).min(3),
    licenceNumber: text(40).min(1),
    licenceExpiry: isoDate,
    status: z.enum(['active', 'inactive']),
};
export const createDriverSchema = z.object({
    ...driverFields,
    status: driverFields.status.default('active'),
});
export const updateDriverSchema = z
    .object(driverFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const listDriversQuerySchema = paginationQuerySchema.extend({
    q: text(50).optional(),
    status: z.enum(['active', 'inactive']).optional(),
});
// ------------------------------------------------------------ routes and stops
const routeFields = {
    name: text(100).min(1),
    vehicleId: uuidSchema.nullable().optional(),
    driverId: uuidSchema.nullable().optional(),
    status: z.enum(['active', 'inactive']),
    notes: optionalText(500),
};
export const createRouteSchema = z.object({
    ...routeFields,
    status: routeFields.status.default('active'),
});
export const updateRouteSchema = z
    .object(routeFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const listRoutesQuerySchema = paginationQuerySchema.extend({
    q: text(50).optional(),
    status: z.enum(['active', 'inactive']).optional(),
});
const stopFields = {
    name: text(100).min(1),
    position: z.number().int().min(1).max(500),
    pickupTime: time,
    dropTime: time,
    monthlyFeeMinor: feeSchema,
};
export const createStopSchema = z.object(stopFields);
export const updateStopSchema = z
    .object(stopFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required');
// --------------------------------------------------------------- assignments
export const createAssignmentSchema = z.object({
    studentId: uuidSchema,
    routeId: uuidSchema,
    stopId: uuidSchema,
    startDate: isoDate.optional(),
});
export const endAssignmentSchema = z.object({ endDate: isoDate.optional() });
export const transferAssignmentSchema = z.object({
    routeId: uuidSchema,
    stopId: uuidSchema,
    effectiveDate: isoDate.optional(),
});
export const listAssignmentsQuerySchema = paginationQuerySchema.extend({
    routeId: uuidSchema.optional(),
    studentId: uuidSchema.optional(),
    status: z.enum(['active', 'ended']).optional(),
});
// ---------------------------------------------------------------- maintenance
export const MAINTENANCE_KINDS = ['service', 'repair', 'inspection', 'insurance', 'other'];
const maintenanceFields = {
    kind: z.enum(MAINTENANCE_KINDS),
    performedOn: isoDate,
    costMinor: feeSchema,
    odometerKm: z.number().int().min(0).max(5_000_000).nullable().optional(),
    vendor: optionalText(200),
    description: text(500).min(1),
    nextDueOn: optionalDate,
};
const nextAfterPerformed = (v) => !v.performedOn || !v.nextDueOn || v.nextDueOn >= v.performedOn;
export const createMaintenanceSchema = z
    .object({ ...maintenanceFields, costMinor: maintenanceFields.costMinor.default(0) })
    .refine(nextAfterPerformed, {
    message: 'The next due date is before the work date',
    path: ['nextDueOn'],
});
export const updateMaintenanceSchema = z
    .object(maintenanceFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required')
    .refine(nextAfterPerformed, {
    message: 'The next due date is before the work date',
    path: ['nextDueOn'],
});
// -------------------------------------------------------------------- charges
export const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');
export const chargeFeesSchema = z.object({
    period: periodSchema,
    dueDate: isoDate.optional(),
    routeId: uuidSchema.optional(),
    studentIds: z.array(uuidSchema).min(1).max(500).optional(),
});
export const listChargesQuerySchema = paginationQuerySchema.extend({
    period: periodSchema.optional(),
    routeId: uuidSchema.optional(),
    studentId: uuidSchema.optional(),
});
export const expiringQuerySchema = z.object({
    days: z.coerce.number().int().min(0).max(365).default(30),
});
/** First and last day of a "YYYY-MM" period. */
export function periodBounds(period) {
    const [y, m] = period.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { first: `${period}-01`, last: `${period}-${String(last).padStart(2, '0')}` };
}
//# sourceMappingURL=transport.js.map